import React, { useEffect, useRef } from 'react';

interface Props {
  children: React.ReactNode;
  onClose: () => void;
  editorOpen: boolean;
}

const BannerVideoDialog: React.FC<Props> = ({
  children,
  onClose,
  editorOpen,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current
      ?.querySelector<HTMLButtonElement>(
        '[aria-label="バナー詳細を閉じる"], [aria-label="動画詳細を閉じる"]'
      )
      ?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [editorOpen]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4"
      style={{ zIndex: editorOpen ? 50 : 1050 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={!editorOpen}
        aria-label="バナーと動画の詳細"
        className="banner-video-dialog relative flex max-h-[calc(100dvh-32px)] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white lg:max-h-[90vh] lg:flex-row"
        onKeyDown={(event) => {
          if (editorOpen) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
          }
          if (event.key !== 'Tab' || !dialogRef.current) return;
          const focusable = Array.from(
            dialogRef.current.querySelectorAll<HTMLElement>(
              'button:not(:disabled), a[href], textarea, input, select, video[controls], [tabindex="0"]'
            )
          ).filter(
            (element) =>
              !element.closest('[aria-hidden="true"]') &&
              element.getClientRects().length > 0
          );
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (
            event.shiftKey &&
            (document.activeElement === first ||
              !focusable.includes(document.activeElement as HTMLElement))
          ) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default BannerVideoDialog;
